/**
 * GoogleAccessDonePage — where a Google consent popup lands: the shared
 * browser-bound landing (components/oauth/OAuthLandingPage, item 66) with
 * Google's mark and wording. Tenant-less and session-less (see paths.ts).
 *
 * A consent no window took is finished here when this popup carries its
 * opener's session: a tab loaded before item 66 opened it and waits in the
 * old protocol, which it is then told in (item 88b).
 */

import { useLanguage } from '@/hooks/useLanguage';
import { OAuthLandingPage, type LandingStatus } from '@/components/oauth/OAuthLandingPage';
import { GoogleDriveGlyph } from '@/modules/conversations/components/drive/driveIcons';
import configService from '@/services/config';
import type { OAuthCallback } from '@/utils/oauthPopup';
import { finishConsentHere } from '../services/staleTabConsent';
import '../components/GoogleAccessPrompt.css';

const OUTCOMES = ['granted', 'declined', 'wrong_account', 'error'] as const;
const KEYS: Record<string, string> = {
  granted: 'landing.granted',
  declined: 'landing.declined',
  wrong_account: 'landing.wrongAccount',
  error: 'landing.error',
  finishing: 'landing.finishing',
  elsewhere: 'landing.elsewhere',
};

/** Stable (module level): the landing page's effect depends on it. */
const finishHere = (callback: OAuthCallback) =>
  finishConsentHere(`${configService.getApiBaseUrl()}/cloud-storage/google_drive/oauth/finish`, callback);

export function GoogleAccessDonePage() {
  const { t } = useLanguage();
  return (
    <OAuthLandingPage
      icon={<GoogleDriveGlyph size={40} />}
      outcomes={OUTCOMES}
      title={(status: LandingStatus) => t(`cloudStorage.access.${KEYS[status] ?? KEYS.error}`)}
      closeHint={t('cloudStorage.access.landing.close')}
      testId="google-access-done"
      finishHere={finishHere}
    />
  );
}
